import type { ChangeRequestFileOperation as Change_Request_File_Operation_Enum } from '@risksmart-app/domain/src/types/consts/change-request-file-operation';
import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { ReactNode } from 'react';
import type {
  DefaultValues,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import type { FormFieldConfigurations } from 'src/contracts/querySelections';
import type { FileOrRelation } from 'src/schemas/global';
import type z from 'zod';

import type { ObjectWithApprovals } from '@/hooks/useChangeRequests';
import type { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import type { CreatedEntityDestination } from '@/utils/createWithFiles';

export enum ButtonVariant {
  Danger = 'Danger',
  Normal = 'Normal',
  Primary = 'Primary',
  Standard = 'Standard',
}
export type SubmitButtonOptions = {
  variant?: ButtonVariant;
  label: string;
  action: () => Promise<void>;
  loading?: boolean;
  disableNotification?: boolean;
  disabled?: boolean;
};

export type CommonProps<TFieldValues extends FieldValues> = Omit<
  FormContextProps<TFieldValues>,
  'renderTemplate'
>;

export type FormTemplateProps<TFieldValues extends FieldValues> =
  FormContextProps<TFieldValues> & { actions: ReactNode };

/**
 * A save action may optionally return the ids of the entities it created or
 * updated.
 * `afterSaveHooks` (e.g. the custom-attribute file upload pipeline) need this
 * to link child records to freshly-created entities whose server-generated ids
 * are not present in the form values. Returning `void` remains valid — on edit
 * the id is read from the form values instead.
 *
 * A save action may also resolve to `MUTATION_NO_OP` to signal that it
 * deliberately did NOT persist (e.g. the approval-config edit is blocked by an
 * in-flight change request). FormContext then suppresses the success toast and
 * leaves the form open instead of reporting a false "changes saved".
 */
export type SaveActionResult = {
  savedIds?: string[];
  /**
   * Set by create surfaces so an after-save file hook can distinguish a known
   * created entity from an ordinary update failure and redirect safely.
   */
  createdEntity?: CreatedEntityDestination;
  /** Navigate only after every after-save hook has settled successfully. */
  successNavigation?: {
    destination: string;
    isReplace?: boolean;
  };
};

export type SaveAction<T> = (
  data: T
) => Promise<SaveActionResult | void | typeof MUTATION_NO_OP>;

type FormFieldsHook<TFieldValues extends FieldValues> = (
  options: UseFormReturn<TFieldValues>
) => FormFields;

type FormField = {
  component: ReactNode;
  hidden?: boolean;
};

export type FormFieldOptions = FormFieldConfigurations;

type FormFields = Record<string, FormField>;

export type SubmitButton<TFieldValues extends FieldValues> = Omit<
  SubmitButtonOptions,
  'action'
> & {
  action: SaveAction<TFieldValues>;
};

export type FormContextProps<TFieldValues extends FieldValues> = {
  testId?: string;
  formId: string;
  header?: ReactNode | string;
  defaultValues: DefaultValues<TFieldValues>;
  values?: TFieldValues;
  // Which success toast a save raises. Defaults to inferring from `values`; set
  // it on a create form that populates `values`, where that reads as an update.
  saveMode?: 'create' | 'update';
  children?: ReactNode;
  fields?: FormFields | FormFieldsHook<TFieldValues>;
  onDismiss?: (saved: boolean) => void;
  onSave: SaveAction<TFieldValues>;
  onDelete?: () => Promise<void>;
  /**
   * Called when the change request is approved.
   */
  onDeleteApproved?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.Schema<any, any>;
  readOnly?: boolean;
  /**
   * Publish this form's save state to the object-toolbar bridge store
   * (`useObjectToolbarFormStore`) so the page-header toolbar's save disc
   * reflects it. Opt-in — set only by the page's primary object form (the
   * detail tab), never by modals/secondary forms, to avoid clobbering the slot.
   * Gate it on the `object_toolbar` feature flag at the call site.
   */
  publishToolbarState?: boolean;
  /**
   * Publish this form's prefillable state to the active-form bridge store
   * (`useActiveFormStore`) so the AI chat side panel (mounted outside this
   * provider) can read the form's fields and write agent-proposed values into
   * it. Opt-in — set only by the page's primary object form, and gate it on the
   * form-prefill feature flag at the call site. Requires `parentType`.
   */
  publishActiveForm?: boolean;
  /**
   * Record id (GUID) of the entity this form edits, or null/undefined on create
   * forms. Forwarded to the active-form bridge (`useActiveFormStore`) so the AI
   * chat panel can tell which record a prefill proposal was made for. Supplied by
   * the opt-in call site — which already resolves it via `useGetGuidParam` — NOT
   * derived from the route, because param names vary and nested routes expose
   * several ids at once. Only meaningful alongside `publishActiveForm`.
   */
  activeFormRecordId?: string | null;
  /**
   * The parent type of the form, used to determine the form field configuration
   */
  parentType?: FormId;
  /**
   * When specified, the form configuration for all possible parent types will be retrieved,
   * avoiding the loading state when switching types which causes the form to loose its state
   */
  possibleParentTypes?: Parent_Type_Enum[];
  aside?: ReactNode;
  renderTemplate: (props: FormTemplateProps<TFieldValues>) => ReactNode;
  submitActions?: SubmitButton<TFieldValues>[];
  secondaryActions?: SubmitButtonOptions[];
  approvalConfig?: {
    object?: ObjectWithApprovals;
  };
  i18n: {
    entity_name: string;
  };
  mapPreviewedChanges?: (
    a: TFieldValues | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    b: any,
    c: (FileOrRelation & {
      changeRequestFileOperation?: Change_Request_File_Operation_Enum;
    })[]
  ) => TFieldValues;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapRequestedChanges?: (requestedChanges: any) => any;
};
